import type { SectionCfg } from "./types";
import type { ExtraSectionSpec } from "./types";
import type { ShopCatalogProduct } from "./templateShopCatalog";
import {
  SHOP_BROWSE_OTHER_SLUG,
  type BreadcrumbNavItem,
  type ShopBrowseIndex,
} from "./shopBreadcrumbs";

export const FEATURED_CATEGORY_TYPE = "featured-category";
export const FEATURED_CATEGORIES_TYPE = "featured-categories";
export const FEATURED_COLLECTIONS_TYPE = "featured-collections";
export const FEATURED_BRANDS_TYPE = "featured-brands";
export const SHOP_BRAND_TYPE = "shop-brand";
export const FEATURED_OUTFIT_TYPE = "featured-outfit";
export const RECENT_LOOKS_TYPE = "recent-looks";

/** Shop-page-only modules (not used on Home / Link in Bio). */
export const SHOP_PAGE_MODULE_TYPES = [
  FEATURED_CATEGORY_TYPE,
  FEATURED_CATEGORIES_TYPE,
  FEATURED_COLLECTIONS_TYPE,
  FEATURED_BRANDS_TYPE,
  SHOP_BRAND_TYPE,
] as const;

export const SHOP_MERCH_CATALOG_TYPES = [
  ...SHOP_PAGE_MODULE_TYPES,
  "trending-products",
  "store-products",
] as const;

/** Product carousel feeds in Shop add/edit (not merch bands). */
export const PRODUCT_FEED_CATALOG_TYPES = [
  "product-feed",
  "trending-products",
  "product-keyword",
  "custom-products",
] as const;

export function isProductFeedCatalogType(catalogType: string): boolean {
  return (PRODUCT_FEED_CATALOG_TYPES as readonly string[]).includes(
    catalogType,
  );
}

/** React section types for Products merch extras (banner + bands + system collections). */
export const SHOP_MERCH_SECTION_TYPES = [
  "FeaturedCategory",
  "FeaturedCategories",
  "FeaturedCollections",
  "FeaturedBrands",
  "ShopBrandCollection",
  "ShopTrendingCollection",
  "StoreProduct",
] as const;

export const CONTENT_LOOKS_CATALOG_TYPES = [
  FEATURED_OUTFIT_TYPE,
  RECENT_LOOKS_TYPE,
] as const;

export const FEATURED_BRANDS_MAX = 4;

export function isShopPageModuleType(catalogType: string): boolean {
  return (SHOP_PAGE_MODULE_TYPES as readonly string[]).includes(catalogType);
}

export function isShopMerchCatalogType(catalogType: string): boolean {
  return (SHOP_MERCH_CATALOG_TYPES as readonly string[]).includes(catalogType);
}

export function isShopMerchSectionType(sectionType: string): boolean {
  return (SHOP_MERCH_SECTION_TYPES as readonly string[]).includes(sectionType);
}

/** System collection cards (not the featured category banner / bands). */
export function isShopCollectionMerchSectionType(sectionType: string): boolean {
  return (
    sectionType === "FeaturedBrands" ||
    sectionType === "ShopBrandCollection" ||
    sectionType === "ShopTrendingCollection"
  );
}

const PAGE_STACK_ANCHORS = new Set([
  "ProductGrid",
  "ContentGrid",
  "PageHeader",
  "Newsletter",
]);

/**
 * Insert index for a new Products / Content component.
 * Goes after components already on the page (oldest → newest). The first one
 * sits above the catalog grid. A fixed index (0 or 1) kept landing new
 * components at the top.
 */
export function appendBelowExistingSections(
  visibleIds: string[],
  sectionTypeById: (id: string) => string | undefined,
): number {
  let lastAdded = -1;
  visibleIds.forEach((id, i) => {
    const type = sectionTypeById(id) || "";
    if (PAGE_STACK_ANCHORS.has(type)) return;
    lastAdded = i;
  });
  if (lastAdded >= 0) return lastAdded + 1;

  const gridIdx = visibleIds.findIndex((id) => {
    const type = sectionTypeById(id) || "";
    return type === "ProductGrid" || type === "ContentGrid";
  });
  if (gridIdx >= 0) return gridIdx;
  return visibleIds.length;
}

/**
 * Shop append insert index among visible sections.
 * New merch goes after whatever is already on the page (oldest → newest),
 * and the first one sits above ProductGrid.
 */
export function shopMerchAppendInsertAt(
  visibleIds: string[],
  sectionTypeById: (id: string) => string | undefined,
): number {
  return appendBelowExistingSections(visibleIds, sectionTypeById);
}

export function isFeaturedBandCatalogType(catalogType: string): boolean {
  return (
    catalogType === FEATURED_CATEGORIES_TYPE ||
    catalogType === FEATURED_COLLECTIONS_TYPE
  );
}

/** Extra section on the Products page (including Trending Products added there). */
export function isShopMerchExtraType(
  catalogType: string,
  pageKey?: string,
): boolean {
  if (pageKey && pageKey !== "shop") return false;
  return (
    isShopPageModuleType(catalogType) || catalogType === "trending-products"
  );
}

export function isContentLooksCatalogType(catalogType: string): boolean {
  return (CONTENT_LOOKS_CATALOG_TYPES as readonly string[]).includes(catalogType);
}

export function shopPageHasMerch(extra: ExtraSectionSpec[] | undefined): boolean {
  if (!Array.isArray(extra) || !extra.length) return false;
  return extra.some((s) => isShopMerchCatalogType(String(s.catalogType || "")));
}

/**
 * Merch that replaces the default product wall until the shopper drills in.
 * Featured Categories / Collections bands sit *above* the catalog (mockup) —
 * they must not hide products.
 */
export function shopPageHidesDefaultCatalog(
  extra: ExtraSectionSpec[] | undefined,
): boolean {
  if (!Array.isArray(extra) || !extra.length) return false;
  return extra.some((s) => {
    const t = String(s.catalogType || "");
    return (
      t === FEATURED_CATEGORY_TYPE ||
      t === FEATURED_BRANDS_TYPE ||
      t === SHOP_BRAND_TYPE ||
      t === "trending-products"
    );
  });
}

/**
 * Products page has top merch bands (Featured Categories / Collections / etc.)
 * that should win the first network burst before the default product catalog.
 */
export function shopPageHasMerchBands(
  sections: { type?: string }[] | undefined,
): boolean {
  if (!Array.isArray(sections) || !sections.length) return false;
  return sections.some((s) => isShopMerchSectionType(String(s.type || "")));
}

export function contentPageHasLooks(extra: ExtraSectionSpec[] | undefined): boolean {
  if (!Array.isArray(extra) || !extra.length) return false;
  return extra.some((s) => isContentLooksCatalogType(String(s.catalogType || "")));
}

export function isContentLooksSectionType(sectionType: string): boolean {
  return sectionType === "FeaturedOutfit" || sectionType === "RecentLooks";
}

/**
 * Content page bands injected above ContentGrid — Looks plus Featured
 * Categories / Collections (same Add + inline placement as Products).
 */
export function isContentInlineBandSectionType(sectionType: string): boolean {
  return (
    isContentLooksSectionType(sectionType) ||
    sectionType === "FeaturedCategories" ||
    sectionType === "FeaturedCollections"
  );
}

export function isContentInlineBandCatalogType(catalogType: string): boolean {
  return (
    isContentLooksCatalogType(catalogType) ||
    isFeaturedBandCatalogType(catalogType)
  );
}

/**
 * Content looks insert index: after looks already on the page, above ContentGrid
 * when this is the first one.
 */
export function contentLooksAppendInsertAt(
  visibleIds: string[],
  sectionTypeById: (id: string) => string | undefined,
  _catalogType?: string,
): number {
  return appendBelowExistingSections(visibleIds, sectionTypeById);
}

export type FeaturedCategoryLevel = "primary" | "secondary";

export type ShopBrandOption = {
  label: string;
  slug: string;
  count: number;
  images: string[];
};

export type FeaturedCategoryChild = {
  label: string;
  slug: string;
  image: string;
  browse: {
    primarySlug: string;
    secondarySlug?: string | null;
    subSlug?: string | null;
  };
};

export function merchCfgString(cfg: SectionCfg | undefined, key: string): string {
  return String(cfg?.[key] ?? "").trim();
}

export function merchCfgStringList(cfg: SectionCfg | undefined, key: string): string[] {
  const raw = cfg?.[key];
  if (Array.isArray(raw)) {
    return raw.map((v) => String(v || "").trim()).filter(Boolean);
  }
  if (typeof raw === "string" && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed.map((v) => String(v || "").trim()).filter(Boolean);
      }
    } catch {
      return raw
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean);
    }
  }
  return [];
}

export function listShopBrandOptions(
  products: ShopCatalogProduct[],
): ShopBrandOption[] {
  const bySlug = new Map<string, ShopBrandOption>();
  for (const product of products) {
    const slug = String(product.breadcrumbs?.brandSlug ?? "").trim().toLowerCase();
    const label = String(product.breadcrumbs?.brand ?? "").trim();
    if (!slug || !label || slug === SHOP_BROWSE_OTHER_SLUG) continue;
    const prev = bySlug.get(slug);
    const image = String(product.image || "").trim();
    if (!prev) {
      bySlug.set(slug, {
        label,
        slug,
        count: 1,
        images: image ? [image] : [],
      });
      continue;
    }
    prev.count += 1;
    if (image && prev.images.length < 4 && !prev.images.includes(image)) {
      prev.images.push(image);
    }
  }
  return [...bySlug.values()].sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
}

/** Brand pills from RPC browse facets (full catalog counts, no product dump). */
export function listShopBrandOptionsFromBrowseIndex(
  index: ShopBrowseIndex,
): ShopBrandOption[] {
  const bySlug = new Map<string, ShopBrandOption>();
  for (const items of Object.values(index.brandsByPathSlug || {})) {
    for (const brand of items) {
      const slug = String(brand.slug || "").trim().toLowerCase();
      const label = String(brand.label || "").trim();
      if (!slug || !label || slug === SHOP_BROWSE_OTHER_SLUG) continue;
      const prev = bySlug.get(slug);
      if (!prev) {
        bySlug.set(slug, {
          label,
          slug,
          count: brand.count ?? 0,
          images: [],
        });
      } else {
        prev.count += brand.count ?? 0;
        if (label.length > prev.label.length) prev.label = label;
      }
    }
  }
  return [...bySlug.values()].sort(
    (a, b) => b.count - a.count || a.label.localeCompare(b.label),
  );
}

/**
 * Prefer browse-index brands (full catalog labels/counts) but fill cover images
 * from loaded products and optional facet sample covers. Browse index alone
 * never carries images — callers that prefer it without merging show empty tiles.
 */
export function mergeShopBrandOptions(
  index: ShopBrowseIndex,
  products: ShopCatalogProduct[],
  coverBySlug?: Map<string, string> | null,
): ShopBrandOption[] {
  const fromIndex = listShopBrandOptionsFromBrowseIndex(index);
  const fromProducts = listShopBrandOptions(products);
  if (!fromIndex.length) {
    if (!coverBySlug?.size) return fromProducts;
    return fromProducts.map((b) => {
      const cover = String(coverBySlug.get(b.slug) || "").trim();
      if (!cover || b.images.includes(cover)) return b;
      return { ...b, images: [cover, ...b.images].slice(0, 4) };
    });
  }
  const bySlug = new Map(fromProducts.map((b) => [b.slug, b]));
  return fromIndex.map((b) => {
    const productImgs = bySlug.get(b.slug)?.images ?? [];
    const cover = String(coverBySlug?.get(b.slug) || "").trim();
    const images: string[] = [];
    if (cover) images.push(cover);
    for (const src of productImgs) {
      if (src && !images.includes(src)) images.push(src);
      if (images.length >= 4) break;
    }
    return { ...b, images };
  });
}

export function productsForBrand(
  products: ShopCatalogProduct[],
  brandSlug: string,
): ShopCatalogProduct[] {
  const want = String(brandSlug || "").trim().toLowerCase();
  if (!want) return [];
  return products.filter(
    (p) => String(p.breadcrumbs?.brandSlug ?? "").trim().toLowerCase() === want,
  );
}

export function featuredCategoryChildren(args: {
  index: ShopBrowseIndex;
  products: ShopCatalogProduct[];
  level: FeaturedCategoryLevel;
  primarySlug: string;
  secondarySlug?: string | null;
}): FeaturedCategoryChild[] {
  const primarySlug = String(args.primarySlug || "").trim().toLowerCase();
  const secondarySlug = String(args.secondarySlug || "").trim().toLowerCase();
  if (!primarySlug || primarySlug === SHOP_BROWSE_OTHER_SLUG) return [];

  const firstImage = (
    pred: (p: ShopCatalogProduct) => boolean,
  ): string => {
    for (const p of args.products) {
      if (!pred(p)) continue;
      const image = String(p.image || "").trim();
      if (image) return image;
    }
    return "";
  };

  if (args.level === "primary") {
    return (args.index.secondariesByPrimarySlug[primarySlug] ?? [])
      .filter((item) => item.slug !== SHOP_BROWSE_OTHER_SLUG)
      .map((item) => ({
        label: item.label,
        slug: item.slug,
        image: firstImage(
          (p) =>
            p.breadcrumbs?.primarySlug === primarySlug &&
            p.breadcrumbs?.secondarySlug === item.slug,
        ),
        browse: { primarySlug, secondarySlug: item.slug },
      }));
  }

  if (!secondarySlug || secondarySlug === SHOP_BROWSE_OTHER_SLUG) return [];
  const pathKey = `${primarySlug}|${secondarySlug}`;
  return (args.index.subsByPathSlug[pathKey] ?? [])
    .filter((item) => item.slug !== SHOP_BROWSE_OTHER_SLUG)
    .map((item) => ({
      label: item.label,
      slug: item.slug,
      image: firstImage(
        (p) =>
          p.breadcrumbs?.primarySlug === primarySlug &&
          p.breadcrumbs?.secondarySlug === secondarySlug &&
          (p.breadcrumbs?.subSlug === item.slug ||
            (!p.breadcrumbs?.subSlug && p.breadcrumbs?.brandSlug === item.slug)),
      ),
      browse: {
        primarySlug,
        secondarySlug,
        subSlug: item.slug,
      },
    }));
}

export function flattenSecondaryOptions(
  index: ShopBrowseIndex,
): { value: string; label: string; primarySlug: string; secondarySlug: string }[] {
  const out: { value: string; label: string; primarySlug: string; secondarySlug: string }[] = [];
  for (const primary of index.primaries) {
    if (primary.slug === SHOP_BROWSE_OTHER_SLUG) continue;
    for (const secondary of index.secondariesByPrimarySlug[primary.slug] ?? []) {
      if (secondary.slug === SHOP_BROWSE_OTHER_SLUG) continue;
      out.push({
        value: `${primary.slug}|${secondary.slug}`,
        label: `${primary.label} · ${secondary.label}`,
        primarySlug: primary.slug,
        secondarySlug: secondary.slug,
      });
    }
  }
  return out;
}

export function findBrowseItemLabel(
  items: BreadcrumbNavItem[],
  slug: string,
): string {
  const want = String(slug || "").trim().toLowerCase();
  return items.find((i) => i.slug === want)?.label || slug;
}
